import { useCallback, useEffect } from 'react';
import { DIAGNOSTICS_DATE_FORMAT } from '@/const';
import { format } from 'date-fns';
import { useSelector } from 'react-redux';
import { useLazyGetAlarmsFiltersQuery, AlarmsFilter } from '@/store/api/alarmsApi';
import {
  setSelectedPriorityOptions,
  setSelectedSeverityOptions,
  setSelectedInterfaceOptions,
} from '@/store/slices/assetsPreviewSlice/alarmsSlice';
import { setSelectedPriority, setSelectedSeverity, setSelectedInterface } from '@/store/slices/externalAlarmsSlice';
import { dispatch, RootState } from '@/store/store';

export const useFetchAlarmsFilters = ({
  filters,
  isExternal = false,
}: {
  filters: AlarmsFilter[];
  isExternal?: boolean;
}) => {
  const {
    [isExternal ? 'dateRange' : 'alarmsDateRange']: { from, to },
  } = useSelector((state: RootState) => state.assetsStudio);

  const { asset_types, asset_ids, component_ids } = useSelector((state: RootState) =>
    isExternal ? state.exteralAlarms : state.alarms
  );

  const [fetchAlarmsFilters, { data, isLoading, isFetching, isError }] = useLazyGetAlarmsFiltersQuery();

  const getAlarmsFiltersData = useCallback(
    (filters: AlarmsFilter[]) => {
      const fromDateISO = format(new Date(from), DIAGNOSTICS_DATE_FORMAT);
      const toDateISO = format(new Date(to), DIAGNOSTICS_DATE_FORMAT);
      fetchAlarmsFilters(
        {
          from_date: fromDateISO,
          to_date: toDateISO,
          asset_types,
          asset_ids,
          component_ids,
          filters,
        },
        true
      )
        .unwrap()
        .catch(() => {});
    },
    [asset_types, asset_ids, component_ids, from, to]
  );

  useEffect(() => {
    getAlarmsFiltersData(filters);
  }, [asset_types, asset_ids, component_ids, from, to]);

  useEffect(() => {
    if (data?.interface) {
      dispatch(isExternal ? setSelectedInterface(data.interface) : setSelectedInterfaceOptions(data.interface));
    }
    if (data?.priority) {
      dispatch(isExternal ? setSelectedPriority(data.priority) : setSelectedPriorityOptions(data.priority));
    }
    if (data?.level) {
      dispatch(
        isExternal
          ? setSelectedSeverity(data.level)
          : setSelectedSeverityOptions(data.level.filter((value: string) => value !== 'informational'))
      );
    }
  }, [data]);

  return {
    data,
    isLoading,
    isFetching,
    isError,
  };
};
